import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { Col, Form, InputNumber, message, Row, Select, Space, Spin } from 'antd';
import { api, endpoints, upload } from '../../../../../../api';
import { CollapseSvg } from '../../../../../../icons';
import { authContext } from '../../../../../../store/authStore/AuthContext';
import {
  CollapseButton,
  CustomButton,
  CustomCheckboxGroup,
  CustomFormLayout,
  CustomInfoLayout,
  DownloadCol,
  FormInput,
  FormItem,
  CloseButton,
  CustomSelect,
  CustomCheckbox,
  GridItem,
  ValidatorFormItem,
  ExpertiseResultsContainer,
} from '../../../../../../styles/styled';
import { authorsFioRegExp, publisherPattern, textbookNameRegExp } from '../../../../../../utils/patterns';
import { sortRegions } from '../../../../../../utils/sortObject';
import { RegionsType } from '../../../../../ExpertPage/types';
import {
  SubjectAreasType,
  BookType,
  firstCheckboxOptions,
  secondCheckboxOptions,
  SubjectsType,
  LanguagesType,
  SchoolClassesType,
  FileDataType,
  TextbookValidatorType,
  PublishersType,
  TextbookLevelsType,
  LoadingFilesType,
  FileJsonNamesType,
  BooksManualType,
} from '../../../../types';
import { BlueBookForm, CloseCollapseTag, NewBookText } from '../../styled';
import UploadBigFileComponent from './UploadBigFileComponent';
import { UploadComponent } from './UploadComponent';
import { FileEditorData } from './UploadComponent/UploadComponent';

type BookFormProps = {
  removeBook: () => void;
  updateBook: (value: BookType) => void;
  validated?: TextbookValidatorType;
  publisherId?: number;
  book?: BookType;
};

const years: number[] = [2010, 2011, 2012, 2013, 2014, 2015, 2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025, 2026];

const { Option } = Select;

const today = new Date();

export const emptyBookTemplate: BookType = {
  link_to_textbook: '',
  comment: '',
  title: '',
  school_classes: [],
  author: '',
  right_holder: '',
  year: '',
  date_public_expertise: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(
    today.getDate(),
  ).padStart(2, '0')}`,
  subject_general: {
    id: 0,
    subject_area_general: {
      id: 0,
    },
  },
  text_book_level: {
    id: 0,
  },
  adaptive: false,
  in_depth_study: false,
  cartographic_material: false,
  hard_copies_present: false,
  installation_manual_present: false,
  public_expertise: false,
  file_textbook_additional_materials: [],
  teacher_guidelines_full_title: '',
  installation_manual_full_title: '',
  digital_media_present: false,
  file_textbook_endpapers: [],
  teacher_guidelines_present: false,
  is_part_of_subject_series: false,
  subject_series_textbooks_titles: '',
};

export const BookForm: FC<BookFormProps> = ({ removeBook, updateBook, validated, book, publisherId }) => {
  const [hideContent, setHide] = useState<boolean>(false);
  const [bookState, setBookState] = useState<BookType>(book || emptyBookTemplate);
  const { state } = useContext(authContext);
  const [subjectAreasState, setSubjectAreasState] = useState<SubjectAreasType>();
  const [subjectsState, setSubjectsState] = useState<SubjectsType>();
  const [languagesState, setLanguagesState] = useState<LanguagesType>();
  const [schoolClassesState, setSchoolClassesState] = useState<SchoolClassesType>();
  const [publishersState, setPublishersState] = useState<PublishersType>([]);
  const [textbookLevelsState, setTextbookLevels] = useState<TextbookLevelsType>([]);
  const [regionsState, setRegions] = useState<RegionsType>([]);
  const [showRegions, setShowRegions] = useState<boolean>(false);
  const [expertiseResultsDisplay, setExpertiseResultsDisplay] = useState<boolean>(false);
  const [showAllExpertise, setShowAllExpertise] = useState<boolean>(false);
  const [loadingFiles, setLoadingFiles] = useState<LoadingFilesType>([]);
  const [displayAddManualBtn, setDisplayBtn] = useState<boolean>(true);
  const [eduMans, setEduMans] = useState<BooksManualType>();
  const [eduMansLoading, setEduMansLoading] = useState<boolean[]>(Array.from({ length: 5 }, () => false));

  const uploadFiles = useCallback(
    async (fileData: FileDataType, fileType: FileJsonNamesType, replaceId?: number, idx?: number) => {
      setLoadingFiles((prevState) => [...prevState, fileType]);

      try {
        const response = await upload.post<{ success: boolean; file_id: number }>(endpoints.addFile(), {
          ...fileData,
          ext: fileData.title.split('.').pop(),
        });

        setLoadingFiles((prevState) => prevState.filter((el) => el !== fileType));

        if (fileType === 'file_textbook_additional_materials') {
          setBookState((prevState) => {
            if (replaceId) {
              return {
                ...prevState,
                file_textbook_additional_materials: prevState.file_textbook_additional_materials.map(
                  (additionalFile, idx) => {
                    return idx === replaceId ? { id: response.data.file_id, title: fileData.title } : additionalFile;
                  },
                ),
              };
            }

            return {
              ...prevState,
              file_textbook_additional_materials: [
                ...prevState.file_textbook_additional_materials,
                { id: response.data.file_id, title: fileData.title },
              ],
            };
          });
        }

        if (idx !== undefined && fileType === 'manual_file') {
          setEduMansLoading(Array.from({ length: 5 }, () => false));

          setEduMans((prevState) => {
            return prevState?.map((el, id) => {
              return id === idx
                ? {
                    ...el,
                    file: {
                      id: response.data.file_id,
                      title: fileData.title,
                    },
                  }
                : el;
            });
          });
        }

        if (fileType !== 'manual_file' && fileType !== 'file_textbook_additional_materials')
          setBookState((prevState) => ({
            ...prevState,
            [fileType]: { id: response.data.file_id, title: fileData.title },
          }));
      } catch (err) {
        message.error('Произошла ошибка при загрузке файла');
        setLoadingFiles((prevState) => prevState.filter((el) => el !== fileType));
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const addManual = () => {
    setEduMans(Array.from({ length: 5 }, () => ({})));
  };

  useEffect(() => {
    setBookState((prevState) => {
      return {
        ...prevState,
        text_book_edu_man: eduMans?.filter(
          (obj) => Object.keys(obj).length === 3 && Object.values(obj).filter((e) => e !== null).length === 3,
        ),
      };
    });
  }, [eduMans]);

  const beforeUploadFile = async (
    data: FileEditorData,
    fileType: FileJsonNamesType,
    replaceId?: number,
    idx?: number,
  ) => {
    if (fileType === 'manual_file' && idx !== undefined)
      setEduMansLoading((prevState) => prevState.map((loading, id) => (id === idx ? !loading : loading)));

    await uploadFiles(
      {
        base64: data.data.split('base64,')[1],
        title: data.name as string,
        ext: data.type as string,
      },
      fileType,
      replaceId,
      idx,
    );
  };

  const removeAdditionalBookFile = (id: number) => {
    setBookState({
      ...bookState,
      file_textbook_additional_materials: bookState.file_textbook_additional_materials.filter((_, idx) => idx !== id),
    });
  };

  const removeBookFileFromState = (
    stateElement: 'file_textbook' | 'file_guideline' | 'file_instruction' | 'file_conclusion' | 'manual_file',
    idx?: number,
  ) => {
    if (stateElement === 'manual_file') {
      setEduMans(
        eduMans?.map((el, id) => {
          return id === idx ? { ...el, file: undefined } : el;
        }),
      );

      return;
    }

    setBookState((prevState) => {
      return { ...prevState, [stateElement]: undefined };
    });
  };

  useEffect(() => {
    (async () => {
      try {
        const areaResponse = await api.get<SubjectAreasType>(endpoints.getSubjectAreas());
        setSubjectAreasState(areaResponse.data);

        const subjectResponse = await api.get<SubjectsType>(endpoints.getSubjects());
        setSubjectsState(subjectResponse.data);

        const languagesResponse = await api.get<LanguagesType>(endpoints.getLanguages());
        setLanguagesState(languagesResponse.data);

        const schoolClassesResponse = await api.get<SchoolClassesType>(endpoints.getSchoolClasses());
        setSchoolClassesState(schoolClassesResponse.data);

        const publishersResponse = await api.get<PublishersType>(endpoints.getPublishers(0, 10000));
        setPublishersState(publishersResponse.data.filter((el) => !el.is_archived));

        const textbookLevelsResponse = await api.get<TextbookLevelsType>(endpoints.getTextbookLevels());
        setTextbookLevels(textbookLevelsResponse.data);

        const regionsResponse = await api.get<RegionsType>(endpoints.getRegions());
        setRegions(regionsResponse.data.sort(sortRegions));
      } catch (err) {}
    })();
  }, [state]);

  useEffect(() => {
    removeBook();

    updateBook(bookState);

    if (bookState.text_book_edu_man && !eduMans) {
      setEduMans(
        Array.from({ length: 5 }, (v, i) => (bookState?.text_book_edu_man ? bookState.text_book_edu_man[i] || {} : {})),
      );
    }

    // eslint-disable-next-line
  }, [bookState]);

  useEffect(() => {
    bookState.public_expertise_score &&
      setBookState({
        ...bookState,
        public_expertise_score: Math.max(1, Math.min(15, bookState.public_expertise_score)),
      });
  }, [bookState]);

  return (
    <>
      <NewBookText>
        Новый учебник
        <CloseCollapseTag>
          <CollapseButton onClick={() => setHide(!hideContent)}>{hideContent ? '—' : <CollapseSvg />}</CollapseButton>
          <CloseButton onClick={() => removeBook()}>+</CloseButton>
        </CloseCollapseTag>
      </NewBookText>
      {!hideContent && (
        <Form>
          <BlueBookForm>
            <Row gutter={[20, 20]}>
              <Col span={12}>
                <FormItem>
                  <span className="one-line-elements">
                    <p>Правообладатель</p>
                    <CustomButton
                      onClick={() =>
                        bookState.publisher
                          ? setBookState({
                              ...bookState,
                              publisher: undefined,
                              right_holder: publishersState.find((el) => el.id === publisherId)?.title || '',
                            })
                          : setBookState({
                              ...bookState,
                              right_holder: publishersState.find((el) => el.id === publisherId)?.title || '',
                              publisher: { id: publisherId || 0 },
                            })
                      }
                      width="300px"
                      padding="3px 10px"
                      marginbottom="5px"
                      fontsize="11px"
                      lowercase
                    >
                      Заполнить как издательство
                    </CustomButton>
                  </span>
                  {!!publishersState.find((el) => el.title === bookState?.right_holder) || bookState?.publisher?.id ? (
                    <CustomSelect
                      onChange={(value, option) => {
                        setBookState({
                          ...bookState,
                          right_holder: value as string,
                          publisher: { id: parseInt(Object.values(option)[0]) },
                        });
                      }}
                      value={
                        publishersState.find(
                          (el) => el.id === bookState?.publisher?.id || el.title === bookState?.right_holder,
                        )?.title || 'Все'
                      }
                      defaultValue="Все"
                      showSearch
                    >
                      <Option key="0" value={0}>
                        Все
                      </Option>
                      {publishersState.map((el) => (
                        <Option key={el.id} value={el.title}>
                          {el.title}
                        </Option>
                      ))}
                    </CustomSelect>
                  ) : (
                    <ValidatorFormItem validator={!publisherPattern.test(bookState.right_holder)}>
                      <FormInput
                        status={validated?.right_holder === false && bookState.right_holder.length === 0 ? 'error' : ''}
                        value={bookState.right_holder}
                        onChange={(e) => setBookState({ ...bookState, right_holder: e.target.value })}
                      />{' '}
                    </ValidatorFormItem>
                  )}
                </FormItem>
              </Col>
              <Col span={12}>
                <ValidatorFormItem
                  validator={bookState.author.length > 0 ? !authorsFioRegExp.test(bookState.author) : false}
                >
                  <p>ФИО автора (авторский коллектив)</p>
                  <FormInput
                    status={validated?.fio === false && bookState.author.length === 0 ? 'error' : ''}
                    value={bookState.author}
                    onChange={(e) => setBookState({ ...bookState, author: e.target.value })}
                  />
                </ValidatorFormItem>
              </Col>
              <Col span={12}>
                <FormItem redstar>
                  <p>Предметная область</p>
                  <Select
                    value={
                      bookState?.subject_general?.subject_area_general?.id === 0
                        ? null
                        : subjectAreasState?.filter(
                            (el) => el.id === bookState?.subject_general?.subject_area_general?.id,
                          )[0]?.title || bookState?.subject_general?.subject_area_general?.title
                    }
                    status={
                      validated?.subject_area_general === false &&
                      bookState?.subject_general?.subject_area_general?.id === 0
                        ? 'error'
                        : ''
                    }
                    loading={subjectAreasState?.length === 0}
                    onChange={(value) => {
                      typeof value === 'number' &&
                        setBookState({
                          ...bookState,
                          subject_general: { id: 0, subject_area_general: { id: value } },
                        });
                    }}
                    placeholder="Не выбрано"
                  >
                    {subjectAreasState?.map((area) => {
                      return (
                        <Option key={area.id} value={area.id}>
                          {area.title}
                        </Option>
                      );
                    })}
                  </Select>
                </FormItem>
              </Col>
              <Col span={12}>
                <FormItem redstar>
                  <p>Учебный предмет</p>
                  <Select
                    value={
                      bookState.subject_general?.id === 0
                        ? null
                        : subjectsState?.filter((el) => el.id === bookState?.subject_general?.id)[0]?.title
                    }
                    showSearch
                    loading={subjectsState?.length === 0}
                    status={validated?.subject_general === false && bookState.subject_general.id === 0 ? 'error' : ''}
                    onChange={(value, option) =>
                      setBookState({
                        ...bookState,
                        subject_general: {
                          id: parseInt(Object.values(option)[0]) as number,
                          subject_area_general: bookState.subject_general.subject_area_general,
                        },
                      })
                    }
                    placeholder="Не выбрано"
                  >
                    {subjectsState?.map((subject) => {
                      return bookState?.subject_general?.subject_area_general?.id === 0 ? (
                        <Option key={subject.id} value={subject.title}>
                          {subject.title}
                        </Option>
                      ) : (
                        subject?.subject_area_general?.id === bookState?.subject_general?.subject_area_general?.id && (
                          <Option key={subject.id} value={subject.title}>
                            {subject.title}
                          </Option>
                        )
                      );
                    })}
                  </Select>
                </FormItem>
              </Col>
              <Col span={12}>
                <ValidatorFormItem validator={!textbookNameRegExp.test(bookState.title)} redstar>
                  <p>Наименование учебника</p>
                  <FormInput
                    status={validated?.title === false && bookState.title.length === 0 ? 'error' : ''}
                    value={bookState.title}
                    onChange={(e) => setBookState({ ...bookState, title: e.target.value })}
                  />
                </ValidatorFormItem>
              </Col>
              <Col span={12}>
                <FormItem>
                  <p>Язык издания учебника</p>
                  <Select
                    allowClear
                    value={
                      bookState?.language?.id === 0
                        ? null
                        : languagesState?.filter((el) => el.id === bookState?.language?.id)[0]?.title
                    }
                    onChange={(value, option) =>
                      setBookState({
                        ...bookState,
                        language: value === undefined ? undefined : { id: Object.values(option)[0] },
                      })
                    }
                    loading={languagesState?.length === 0}
                    placeholder="Не выбрано"
                    showSearch
                  >
                    {languagesState?.map((lang) => {
                      return (
                        <Option key={lang.id} value={lang.title}>
                          {lang.title}
                        </Option>
                      );
                    })}
                  </Select>
                </FormItem>
              </Col>
              <Col span={12}>
                <FormItem redstar>
                  <p>Уровень содержания учебника</p>
                  <Select
                    onChange={(value, option) => {
                      setBookState({
                        ...bookState,
                        text_book_level: { id: parseInt(Object.values(option)[0]) },
                      });
                    }}
                    loading={textbookLevelsState.length === 0}
                    value={
                      bookState.text_book_level
                        ? textbookLevelsState.find((el) => el.id === bookState?.text_book_level?.id)?.title
                        : 'Все'
                    }
                    defaultValue="Не указан"
                    showSearch
                  >
                    {textbookLevelsState.map((el) => (
                      <Option key={el.id} value={el.title}>
                        {el.title}
                      </Option>
                    ))}
                  </Select>
                </FormItem>
              </Col>
              <Col span={12}>
                <FormItem redstar>
                  <p>Класс</p>

                  <Select
                    mode="multiple"
                    allowClear
                    status={validated?.school_classes === false && bookState.school_classes.length === 0 ? 'error' : ''}
                    value={bookState?.school_classes
                      ?.map((el) => schoolClassesState?.find((e) => e?.id?.toString() === el?.id?.toString()))
                      .map((el) => {
                        return el?.title?.toString();
                      })}
                    maxTagCount="responsive"
                    loading={schoolClassesState?.length === 0}
                    onChange={(value, option) => {
                      setBookState({
                        ...bookState,
                        school_classes: option.map((el) => {
                          return { id: Object.values(el)[0] as string };
                        }),
                      });
                    }}
                    placeholder="Не выбрано"
                  >
                    {schoolClassesState?.map((schoolClass) => {
                      return (
                        <Option key={schoolClass.id} value={schoolClass.title}>
                          {schoolClass.title}
                        </Option>
                      );
                    })}
                  </Select>
                </FormItem>
              </Col>
              <Col span={12}>
                <FormItem redstar>
                  <p>Год издания</p>
                  <Select
                    value={bookState.year}
                    status={validated?.year === false && bookState.year.length === 0 ? 'error' : ''}
                    onChange={(value: string) =>
                      setBookState({
                        ...bookState,
                        year: value,
                      })
                    }
                    placeholder="Не выбрано"
                  >
                    {years.reverse().map((el) => {
                      return (
                        <Option value={el} key={el}>
                          {el}
                        </Option>
                      );
                    })}
                  </Select>
                </FormItem>
              </Col>
              <Col span={24}>
                <CustomCheckboxGroup
                  onChange={(value) => {
                    setBookState({
                      ...bookState,
                      adaptive: value.includes('adaptive'),
                      in_depth_study: value.includes('in_depth_study'),
                      hard_copies_present: value.includes('hard_copies_present'),
                      cartographic_material: value.includes('cartographic_material'),
                    });
                  }}
                  value={[
                    !!bookState?.adaptive ? 'adaptive' : '',
                    !!bookState?.in_depth_study ? 'in_depth_study' : '',
                    !!bookState?.cartographic_material ? 'cartographic_material' : '',
                  ]}
                  options={firstCheckboxOptions}
                />
              </Col>
              <Col span={24}></Col>
              <Col span={24}>
                <GridItem align="flex-start" justify="flex-start" direction="column">
                  <CustomCheckbox
                    checked={bookState?.is_part_of_subject_series || false}
                    onChange={(e) =>
                      setBookState({
                        ...bookState,
                        is_part_of_subject_series: e.target.checked,
                      })
                    }
                  >
                    Учебник относится к завершенной предметной линии
                  </CustomCheckbox>
                  {bookState.is_part_of_subject_series && (
                    <FormItem padding="5px 0 20px 0">
                      <div className="text"> Наименования учебников, образующих завершенную предметную линию</div>
                      <FormInput
                        value={bookState?.subject_series_textbooks_titles}
                        onChange={(e) =>
                          setBookState({ ...bookState, subject_series_textbooks_titles: e.target.value })
                        }
                      />
                    </FormItem>
                  )}
                  <CustomCheckbox
                    checked={showRegions || !!bookState?.region?.id || false}
                    onChange={(e) => setShowRegions(e.target.checked)}
                  >
                    Учебник, учитывающий региональные особенности
                  </CustomCheckbox>
                  {(showRegions || bookState?.region?.id) && (
                    <FormItem padding="5px 0 20px 0">
                      <CustomSelect
                        showSearch
                        value={regionsState?.find((el) => el.id.toString() === bookState?.region?.id.toString())?.title}
                        onChange={(value, options) =>
                          setBookState({
                            ...bookState,
                            region: { id: Object.values(options)[0] },
                          })
                        }
                      >
                        {regionsState?.map((el) => (
                          <Option key={el.id} value={el.title}>
                            {el.title}
                          </Option>
                        ))}
                      </CustomSelect>
                    </FormItem>
                  )}
                </GridItem>
              </Col>
              <Col span={24}>
                <CustomCheckboxGroup
                  onChange={(value) => {
                    setBookState({
                      ...bookState,
                      public_expertise: value.includes('public_expertise'),
                      installation_manual_present: value.includes('installation_manual_present'),
                      teacher_guidelines_present: value.includes('teacher_guidelines_present'),
                    });
                  }}
                  value={[
                    !!bookState?.public_expertise && 'public_expertise',
                    !!bookState?.installation_manual_present && 'installation_manual_present',
                    !!bookState?.teacher_guidelines_present && 'teacher_guidelines_present',
                  ]}
                  options={secondCheckboxOptions}
                />
              </Col>
              <Col span={24}>
                <CustomFormLayout>Предоставление документов в электронном формате</CustomFormLayout>
              </Col>
              <DownloadCol span={24} style={{ paddingLeft: '35px' }} paddingBottom={35}>
                <CustomInfoLayout>Учебник в электронной форме*</CustomInfoLayout>
                <UploadBigFileComponent
                  formats={['.pdf']}
                  title={bookState?.file_textbook?.title}
                  setBook={(file_id, title) =>
                    setBookState((prevState) => {
                      return { ...prevState, file_textbook: { id: file_id, title: title } };
                    })
                  }
                />
              </DownloadCol>
              <DownloadCol span={24} style={{ paddingLeft: '35px' }} paddingBottom={35}>
                <CustomInfoLayout>Учебное пособие (при наличии)</CustomInfoLayout>
                <DownloadCol style={{ border: 0 }} span={24}>
                  {eduMans?.map((el, idx) => (
                    <Row key={idx} gutter={[0, 10]}>
                      <Col span={24}>
                        <ValidatorFormItem validator={!textbookNameRegExp.test(bookState.title)}>
                          <p>{`Наименование учебного пособия ${idx + 1}`}</p>
                          <FormInput
                            maxLength={255}
                            value={el.title}
                            onChange={(e) =>
                              setEduMans(
                                eduMans.map((el, id) => {
                                  return id === idx ? { ...el, title: e.target.value } : el;
                                }),
                              )
                            }
                          />
                        </ValidatorFormItem>
                      </Col>
                      <Col span={24}>
                        <ValidatorFormItem
                          validator={bookState.author.length > 0 ? !authorsFioRegExp.test(bookState.author) : false}
                        >
                          <p>{`ФИО автора (авторский коллектив) учебного пособия ${idx + 1}`}</p>
                          <FormInput
                            maxLength={255}
                            value={el.fio}
                            onChange={(e) =>
                              setEduMans(
                                eduMans.map((el, id) => {
                                  return id === idx ? { ...el, fio: e.target.value } : el;
                                }),
                              )
                            }
                          />
                        </ValidatorFormItem>
                      </Col>
                      <Col>
                        <Space align="center">
                          {eduMansLoading[idx] ? (
                            <Spin size="default" />
                          ) : (
                            <UploadComponent
                              onClear={() => removeBookFileFromState('manual_file', idx)}
                              formats={['.pdf']}
                              value={eduMans[idx].file}
                              onChange={(data) => beforeUploadFile(data, 'manual_file', undefined, idx)}
                            />
                          )}
                        </Space>
                        <p>рекомендуется прикреплять файл в формате .pdf</p>
                      </Col>
                    </Row>
                  ))}
                </DownloadCol>
                <CustomButton type="button" none={!!eduMans} onClick={addManual} width="800px" lined>
                  Добавить пособие
                </CustomButton>
              </DownloadCol>
              <DownloadCol span={24} style={{ paddingLeft: '35px' }}>
                <CustomInfoLayout>
                  Инструкция по установке, настройке и использованию электронной формы учебника*
                </CustomInfoLayout>
                <div className="text">Полное наименование инструкции</div>
                <FormInput
                  style={{ marginBottom: '15px' }}
                  value={bookState.installation_manual_full_title}
                  onChange={(e) =>
                    setBookState((prevState) => {
                      return { ...prevState, installation_manual_full_title: e.target.value };
                    })
                  }
                />
                <Space align="center">
                  {loadingFiles.includes('file_instruction') ? (
                    <Spin />
                  ) : (
                    <UploadComponent
                      onClear={() => removeBookFileFromState('file_instruction')}
                      value={bookState.file_instruction}
                      formats={['.pdf']}
                      onChange={(data) => beforeUploadFile(data, 'file_instruction')}
                    />
                  )}
                </Space>
                <p>рекомендуется прикреплять файл в формате .pdf</p>
              </DownloadCol>

              <DownloadCol span={24} style={{ paddingLeft: '35px' }}>
                <CustomInfoLayout>Методическое пособие для учителя*</CustomInfoLayout>
                <div className="text">Полное наименование пособия</div>
                <FormInput
                  style={{ marginBottom: '15px' }}
                  value={bookState.teacher_guidelines_full_title}
                  onChange={(e) =>
                    setBookState((prevState) => {
                      return { ...prevState, teacher_guidelines_full_title: e.target.value };
                    })
                  }
                />
                <Space align="center">
                  {loadingFiles.includes('file_guideline') ? (
                    <Spin />
                  ) : (
                    <UploadComponent
                      onClear={() => removeBookFileFromState('file_guideline')}
                      value={bookState.file_guideline}
                      formats={['.pdf']}
                      onChange={(data) => beforeUploadFile(data, 'file_guideline')}
                    />
                  )}
                </Space>
                <p>рекомендуется прикреплять файл в формате .pdf</p>
              </DownloadCol>

              <DownloadCol span={24} style={{ paddingLeft: '35px' }}>
                <CustomInfoLayout>Дополнительные материалы к учебнику</CustomInfoLayout>

                {loadingFiles.includes('file_textbook_additional_materials') ? (
                  <Spin />
                ) : (
                  <Space wrap align="end">
                    <Space direction="vertical">
                      {bookState.file_textbook_additional_materials?.map((_, idx) => (
                        <UploadComponent
                          key={idx}
                          onClear={() => removeAdditionalBookFile(idx)}
                          value={bookState.file_textbook_additional_materials[idx]}
                          formats={['.pdf']}
                          onChange={(data) => beforeUploadFile(data, 'file_textbook_additional_materials', idx)}
                        />
                      ))}
                    </Space>
                    <UploadComponent
                      formats={['.pdf']}
                      onChange={(data) => beforeUploadFile(data, 'file_textbook_additional_materials')}
                    />
                  </Space>
                )}
                <p>рекомендуется прикреплять файл в формате .pdf</p>
              </DownloadCol>

              <DownloadCol span={24} paddingBottom={20}>
                <GridItem justify="flex-start" align="center" direction="row">
                  <CustomInfoLayout>Результаты общественной экспертизы</CustomInfoLayout>
                  {showAllExpertise && (
                    <CloseButton onClick={() => setShowAllExpertise(false)} pressed positioned>
                      +
                    </CloseButton>
                  )}
                </GridItem>
                <CustomButton
                  type="button"
                  none={!displayAddManualBtn}
                  onClick={() => {
                    setExpertiseResultsDisplay(true);
                    setDisplayBtn(!displayAddManualBtn);
                  }}
                  width="800px"
                  lined
                >
                  Добавить
                </CustomButton>
                <ExpertiseResultsContainer display={expertiseResultsDisplay}>
                  <Space align="center">
                    <UploadComponent
                      onClear={() => removeBookFileFromState('file_conclusion')}
                      value={bookState.file_conclusion}
                      formats={['.pdf']}
                      onChange={(data) => beforeUploadFile(data, 'file_conclusion')}
                    />
                  </Space>
                  <p>рекомендуется прикреплять файл в формате .pdf</p>
                  {(showAllExpertise || bookState?.file_conclusion) && (
                    <p className="margined">Суммарный балл по результатам общественной экспертизы</p>
                  )}
                  {(showAllExpertise || bookState?.file_conclusion) && (
                    <InputNumber
                      defaultValue={1}
                      controls={false}
                      onChange={(e) => {
                        setBookState({ ...bookState, public_expertise_score: e });
                      }}
                      value={bookState?.public_expertise_score || 1}
                      width={'60px'}
                      type="number"
                    />
                  )}
                </ExpertiseResultsContainer>
              </DownloadCol>
              <Col span={12}>
                <CustomInfoLayout>Ссылка на электронную форму учебника</CustomInfoLayout>
                <FormInput
                  value={bookState.link_to_textbook}
                  onChange={(e) => setBookState({ ...bookState, link_to_textbook: e.target.value })}
                  placeholder="https://"
                />
              </Col>
              <Col span={12}>
                <CustomInfoLayout>Комментарий</CustomInfoLayout>
                <FormInput
                  value={bookState.comment}
                  onChange={(e) => setBookState({ ...bookState, comment: e.target.value })}
                />
              </Col>
            </Row>
          </BlueBookForm>
        </Form>
      )}
    </>
  );
};
